import { makeSimpleClosedTask, normalizeMay2026Variant, shuffle } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const ALL_DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function randomDigitSet() {
  const includesZero = Math.random() < 0.5;
  const size = 5 + Math.floor(Math.random() * 4);
  const pool = shuffle(ALL_DIGITS.filter((digit) => digit !== 0));
  const digits = includesZero ? [0, ...pool.slice(0, size - 1)] : pool.slice(0, size);
  const sorted = digits.sort((left, right) => left - right);
  const oddCount = sorted.filter((digit) => digit % 2 === 1).length;
  return oddCount > 0 && oddCount < sorted.length ? sorted : randomDigitSet();
}

function factorLatex(factors) {
  return factors.join("\\cdot");
}

function buildAnswerForms({ digitCount, firstDigitCount, oddCount, answerStyle }) {
  const correctFactors = [firstDigitCount, digitCount, oddCount];
  const correctValue = correctFactors.reduce((product, factor) => product * factor, 1);
  const evenCount = digitCount - oddCount;
  const candidateFactors = [
    [digitCount, digitCount, oddCount],
    [firstDigitCount, digitCount, digitCount],
    [digitCount, digitCount, digitCount],
    [firstDigitCount, oddCount, oddCount],
    [firstDigitCount, digitCount - 1, oddCount],
    [firstDigitCount, firstDigitCount, oddCount],
    [firstDigitCount, digitCount, evenCount],
  ];
  const seenValues = new Set([correctValue]);
  const wrongFactors = candidateFactors.filter((factors) => {
    const value = factors.reduce((product, factor) => product * factor, 1);
    if (value <= 0 || seenValues.has(value)) return false;
    seenValues.add(value);
    return true;
  }).slice(0, 3);
  const format = answerStyle === "product"
    ? factorLatex
    : (factors) => String(factors.reduce((product, factor) => product * factor, 1));

  return {
    correct: format(correctFactors),
    correctFactors,
    correctValue,
    wrong: wrongFactors.map(format),
  };
}

export function generateTask(options = {}) {
  const variant = normalizeMay2026Variant(options);
  const digits = variant === "original" ? [0, 1, 2, 3, 4, 5, 6] : randomDigitSet();
  const digitCount = digits.length;
  const firstDigitCount = digitCount - (digits.includes(0) ? 1 : 0);
  const oddCount = digits.filter((digit) => digit % 2 === 1).length;
  const answerStyle = variant === "original"
    ? "product"
    : options.answerStyle === "product" || options.answerStyle === "value"
      ? options.answerStyle
      : Math.random() < 0.5 ? "product" : "value";
  const answerForms = buildAnswerForms({
    digitCount,
    firstDigitCount,
    oddCount,
    answerStyle,
  });
  const fixedOptions = variant === "original" ? [
    ["A", "6\\cdot7\\cdot3"],
    ["B", "6\\cdot7\\cdot7"],
    ["C", "7\\cdot7\\cdot3"],
    ["D", "7\\cdot7\\cdot7"],
  ] : null;
  return makeSimpleClosedTask({
    type: "MaturaOddThreeDigitCountingClosedTask",
    taskNumber: "29",
    variant,
    prompt: "Wszystkich liczb naturalnych trzycyfrowych nieparzystych, w których zapisie dziesiętnym występują tylko cyfry",
    promptLatex: digits.join(",\\ "),
    promptAfterLatex: ", jest:",
    correct: answerForms.correct,
    wrong: answerForms.wrong,
    fixedOptions,
    tags: ["kombinatoryka", "regula mnozenia"],
    explanationSteps: [{
      title: digits.includes(0)
        ? "Na miejscu setek nie może stać zero. Cyfra dziesiątek jest dowolna, a cyfra jedności musi być nieparzysta."
        : "Każda dozwolona cyfra może stać na miejscu setek i dziesiątek, a cyfra jedności musi być nieparzysta.",
      latex: `${factorLatex(answerForms.correctFactors)}=${answerForms.correctValue}`,
    }],
    data: {
      digits,
      digitCount,
      firstDigitCount,
      oddCount,
      answerStyle,
      answerValue: answerForms.correctValue,
    },
  });
}

export { checkClosedAnswer as checkAnswer, renderClosedAnswer as renderAnswer, renderClosedExplanation as renderExplanation, renderClosedInput as renderInput, renderClosedTask as renderTask, renderClosedUserAnswer as renderUserAnswer } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
